import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import EntityLogs from "../../../../../../../../components/Common/EntityLogs/EntityLogs";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { LoanApi } from "../../../../../../../../services/Api/types";
import { selectUser } from "../../../../../../../../services/users/selectors";
import { downloadFile } from "../../../../../../../../utils";
import LoanPreview from "../../../../../../components/Loan/LoanPreview/LoanPreview";
import SendLoanPdfViaEmailModal from "../../../../../../components/Loan/SendLoanPdfViaEmailModal";
import SendMailWithLoanToClient from "../../../../../../components/Loan/SendMailWithLoanToClient";
import { FormatLoanStatuses } from "../../../../../Partner/pages/Preview/components/FormatLoanStatus";

interface MatchParams {
	id: string;
}

const AdminLoansPreviewPage = () => {
	const [loan, setLoan] = useState<undefined | LoanApi>(undefined);
	const [showLogs, setShowLogs] = useState(false);
	const [showSendViaEmailModal, setShowSendViaEmailModal] = useState(false);
	const [showSendLoanEmailModal, setShowSendLoanEmailModal] = useState(false);
	const [showCancelLoanModal, setShowCancelLoanModal] = useState(false);
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const confirmation = useConfirmation();
	const user = useSelector(selectUser);
	const { addFlash, addSuccessFlash } = useFlash();
	const params = { include: "organization,partner,items,attachments" };
	const { id } = useParams<MatchParams>();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		onFetchLoan();
	}, []);

	const onFetchLoan = async () => {
		try {
			const res = await adminApi.getLoan(Number(id), params);
			setLoan(res);
			handleChangeTabTitle(
				`#${res.reference_id} | ${t("modules.loan.header.title")} | ${t("lib:go_component.navigation.admin")}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!loan) return <LoadingContainer />;

	const onOpen = async () => {
		try {
			setLoading(true);
			const resp = await adminApi.openLoan(loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const onClose = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			setLoading(true);
			const resp = await adminApi.closeLoan(loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const onRevoke = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("modules.loan.field.confirmation_revoke_loan.title"),
			});
			setLoading(true);
			const resp = await adminApi.cancelAcceptLoan(loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const downloadPdf = async () => {
		await downloadFile(
			() => adminApi.getLoanPdf(loan.id),
			`${t("modules.loan.field.loan.title")}_${loan.id}.pdf`,
			addFlash
		);
	};

	const createSubscription = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("modules.loan.field.confirmation_loan_close.title", {
					count: loan.items.length,
					type: loan.license_level,
				}),
			});
			await adminApi.createSubscriptionFromLoan(loan.id);
			addFlash({
				type: "success",
				msg: t("modules.loan.field.loan_closed.title", {
					count: loan.items.length,
					type: loan.license_level,
				}),
			});
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const isCloseLoanButtonVisible = () => {
		if (loan.status === "ACCEPTED") {
			return user?.admin || user?.roles?.includes("POSNET") || user?.roles?.includes("OFFICE");
		}
		return false;
	};

	const onRejectLoanCancellation = async () => {
		try {
			const res = await adminApi.rejectLoanCancellation(loan.id, params);
			setLoan(res);
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onApproveLoanCancellation = async () => {
		try {
			const res = await adminApi.approveLoanCancellation(loan.id, params);
			setLoan(res);
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const renderTitle = () => {
		return (
			<>
				{t("modules.loan.header.title")} #{loan.reference_id}
				<FormatLoanStatuses loan={loan} />
			</>
		);
	};

	const buttons: ButtonProps[] = [
		{
			path: `/admin/loans/${loan.id}/edit`,
			title: t("common.action.edit", { ns: "lib" }),
			hide: loan.status === "CLOSED",
			variant: "light",
		},
		{
			hide: loan.status !== "DRAFT",
			loading,
			title: t("common.action.confirm", { ns: "lib" }),
			action: onOpen,
			variant: "primary",
		},
		{
			hide: !isCloseLoanButtonVisible(),
			loading,
			title: t("common.action.close", { ns: "lib" }),
			action: onClose,
			variant: "primary",
		},
		{
			hide: loan.status !== "ACCEPTED",
			title: t("modules.loan.action.revoke_loan_to_partner.title"),
			dropdown: true,
			action: onRevoke,
		},
		{
			hide: loan.status === "CLOSED",
			title: t("common.action.download_pdf"),
			action: downloadPdf,
			dropdown: true,
		},
		{
			hide: !(loan.status === "CLOSED" && user?.admin),
			action: createSubscription,
			title: t("modules.loan.action.create_subscription.title"),
			dropdown: true,
		},
		{
			hide: !(loan.status !== "CANCELLED" && loan.status !== "CANCELLATION_PENDING"),
			title: t("modules.loan.action.cancel_loan.title"),
			action: () => setShowCancelLoanModal(true),
			dropdown: true,
		},
		{
			action: () => setShowLogs(!showLogs),
			title: t("common.word.logs", { ns: "lib" }),
			dropdown: true,
		},
		{
			title: t("common.action.send_via_email"),
			action: () => setShowSendViaEmailModal(true),
			dropdown: true,
		},
		{
			hide: loan.status !== "CANCELLATION_PENDING",
			action: onApproveLoanCancellation,
			title: t("modules.loan.action.approve_loan_cancellation.title"),
			dropdown: true,
		},
		{
			hide: loan.status !== "CANCELLATION_PENDING",
			title: t("modules.loan.action.reject_loan_cancellation.title"),
			action: onRejectLoanCancellation,
			dropdown: true,
		},
		{
			hide: loan.status !== "OPEN",
			title: t("common.action.send_mail_to_client"),
			action: () => setShowSendLoanEmailModal(true),
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			path: `/admin/loans/${loan.id}/edit`,
			title: t("common.action.edit", { ns: "lib" }),
			hide: loan.status === "CLOSED",
		},
		{
			hide: loan.status !== "DRAFT",
			title: t("common.action.confirm", { ns: "lib" }),
			action: onOpen,
		},
		{
			hide: !isCloseLoanButtonVisible(),
			title: t("common.action.close", { ns: "lib" }),
			action: onClose,
		},
		{
			hide: loan.status !== "ACCEPTED",
			title: t("modules.loan.action.revoke_loan_to_partner.title"),
			action: onRevoke,
		},
		{
			hide: loan.status === "CLOSED",
			title: t("common.action.download_pdf"),
			action: downloadPdf,
		},
		{
			hide: !(loan.status === "CLOSED" && user?.admin),
			action: createSubscription,
			title: t("modules.loan.action.create_subscription.title"),
		},
		{
			hide: !(loan.status !== "CANCELLED" && loan.status !== "CANCELLATION_PENDING"),
			title: t("modules.loan.action.cancel_loan.title"),
			action: () => setShowCancelLoanModal(true),
		},
		{
			action: () => setShowLogs(!showLogs),
			title: t("common.word.logs", { ns: "lib" }),
		},
		{
			title: t("common.action.send_via_email"),
			action: () => setShowSendViaEmailModal(true),
		},
		{
			hide: loan.status !== "CANCELLATION_PENDING",
			action: onApproveLoanCancellation,
			title: t("modules.loan.action.approve_loan_cancellation.title"),
		},
		{
			hide: loan.status !== "CANCELLATION_PENDING",
			title: t("modules.loan.action.reject_loan_cancellation.title"),
			action: onRejectLoanCancellation,
		},
		{
			hide: loan.status !== "OPEN",
			title: t("common.action.send_mail_to_client"),
			action: () => setShowSendLoanEmailModal(true),
		},
	];

	return (
		<div className={"content-preview"}>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			{showLogs && (
				<EntityLogs resourceId={loan.id} resourceType={"LOAN"} onHide={() => setShowLogs(!showLogs)} />
			)}
			{showSendViaEmailModal && (
				<SendLoanPdfViaEmailModal
					isShown={showSendViaEmailModal}
					onHide={() => setShowSendViaEmailModal(false)}
					loanId={loan.id}
				/>
			)}
			{showSendLoanEmailModal && (
				<SendMailWithLoanToClient
					isShown={showSendLoanEmailModal}
					onHide={() => setShowSendLoanEmailModal(false)}
					loanId={loan.id}
					organizationEmail={loan.organization.company_email}
				/>
			)}
			<LoanPreview
				handleUpdateLoan={setLoan}
				loan={loan}
				setShowCancelLoanModal={setShowCancelLoanModal}
				showCancelLoanModal={showCancelLoanModal}
				onFetchLoan={onFetchLoan}
				admin
			/>
		</div>
	);
};
export default AdminLoansPreviewPage;
